import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { AppErrorType, captureException } from '../../service/ErrorsReporting/ErrorsReporting';
import { Size } from '../../typings/common';
import { BannerViewType, PcodeAds } from '../../typings/pcode';
import { CanvasApi } from '../api/CanvasApi';
import { PcodeStaticCanvasPreview } from '../PcodeStaticCanvasPreview/PcodeStaticCanvasPreview';
import { PcodeVideoCanvasPreview } from '../PcodeVideoCanvasPreview/PcodeVideoCanvasPreview';

import './CreativePreview.css';

const BLOCK = 'creative-preview';

export type CreativePreviewProps = {
    creativeId: string;
    batchId: string;
    clientId?: string;
    width: number | undefined;
    height: number | undefined;
    previewType: BannerViewType;
    ad?: PcodeAds;
    isSquareFormat: boolean;
    cspNonce: string;
};

type CreativePreviewState = {
    previewUrl: string;
    html5: string;
    size: Size;
};

export class CreativePreview extends React.PureComponent<CreativePreviewProps, CreativePreviewState> {
    private api = new CanvasApi().preview;
    private videoPreviewRef = React.createRef<PcodeVideoCanvasPreview>();
    private iframeHref = React.createRef<HTMLIFrameElement>();

    constructor(props: CreativePreviewProps) {
        super(props);
        this.state = {
            previewUrl: '',
            html5: '',
            size: {
                width: this.props.width || 0,
                height: this.props.height || 0
            }
        };
    }

    componentDidMount(): void {
        this.getPreviewInfo();
    }

    componentDidUpdate(prevProps: Readonly<CreativePreviewProps>, prevState: Readonly<CreativePreviewState>): void {
        if (
            prevState.html5 !== this.state.html5 &&
            this.iframeHref.current &&
            this.iframeHref.current.contentDocument
        ) {
            this.iframeHref.current.contentDocument.writeln(this.state.html5);
        }
    }

    componentWillUnmount(): void {
        if (this.videoPreviewRef.current) {
            this.videoPreviewRef.current.onExternalDestroyPlayer();
        }
    }

    render() {
        if (this.props.previewType === BannerViewType.adaptive0418 && !this.state.previewUrl) {
            return null;
        }

        const { width, height } = this.props;

        switch (this.props.previewType) {
            case BannerViewType.adaptive0418:
                return (
                    <div style={{ width, height }}>
                        <PcodeStaticCanvasPreview previewUrl={this.state.previewUrl} currentSize={this.state.size} />
                    </div>
                );

            case BannerViewType.videoAdditionPreview:
                return (
                    <div style={{ width, height }}>
                        <PcodeVideoCanvasPreview ref={this.videoPreviewRef} isAudio={this.props.isSquareFormat} />
                    </div>
                );

            case BannerViewType.html5CreativePreview:
                return (
                    <div style={{ width, height }} className={Bem.cls(BLOCK, 'html5-creative-preview')}>
                        <iframe
                            frameBorder="0"
                            ref={this.iframeHref}
                            width={this.state.size.width}
                            height={this.state.size.height}
                        />
                    </div>
                );

            default:
                break;
        }

        return null;
    }

    private startVideo(vast: string) {
        if (this.videoPreviewRef.current && vast) {
            this.videoPreviewRef.current.onExternalInitPlayer({
                vast: vast,
                autostartoff: false
            });
        }
    }

    private async getPreviewInfo() {
        const { batchId, creativeId, clientId = '', previewType, ad, cspNonce } = this.props;

        try {
            switch (previewType) {
                case BannerViewType.adaptive0418:
                    const previewInfo = await this.api.getPreviewInfo(batchId, creativeId);
                    this.setState({ previewUrl: previewInfo.creativeURL, size: previewInfo.data });

                    break;
                case BannerViewType.videoAdditionPreview:
                    const previewAd = ad || {
                        domain: '',
                        punyDomain: '',
                        url: '',
                        warning: ''
                    };
                    const videoPreviewInfo = await this.api.getVideoPreviewInfo(batchId, clientId, previewAd);
                    this.startVideo(videoPreviewInfo.vast);

                    break;
                case BannerViewType.html5CreativePreview:
                    const html5PreviewInfo = await this.api.getHtml5Preview(batchId, creativeId, cspNonce);
                    this.setState({
                        html5: html5PreviewInfo.content,
                        size: { width: html5PreviewInfo.width, height: html5PreviewInfo.height }
                    });

                    break;
                default:
                    break;
            }
        } catch (e) {
            captureException(e, {
                type: AppErrorType.creativePreviewError,
                extra: {
                    creativeId: this.props.creativeId
                }
            });
        }
    }
}
